import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  getAdminStandardSets,
  postAdminStandardSet,
  type StandardSetSummary,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";

export default function AdminStandardsPage() {
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const [sets, setSets] = useState<StandardSetSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [jurisdiction, setJurisdiction] = useState("");
  const [gradeBand, setGradeBand] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!bearer) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getAdminStandardSets(bearer);
      setSets(data.standardSets);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load standard sets");
    } finally {
      setLoading(false);
    }
  }, [bearer]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleCreate() {
    if (!bearer || busy || !name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await postAdminStandardSet(bearer, {
        name: name.trim(),
        jurisdiction: jurisdiction.trim() || undefined,
        gradeBand: gradeBand.trim() || undefined,
      });
      setName("");
      setJurisdiction("");
      setGradeBand("");
      setCreating(false);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Create failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card card-wide">
      <header className="row-header">
        <div>
          <h1>Standards &amp; glossary</h1>
          <p className="lede">
            Upload a standards PDF and let AI extract a reviewable set of standards and glossary terms for teachers to
            align lessons to.
          </p>
        </div>
          <button type="button" className="btn primary" onClick={() => setCreating((v) => !v)}>
            {creating ? "Cancel" : "New standard set"}
          </button>
        </header>

        {error ? (
          <div className="warn-banner" role="alert">
            {error}
          </div>
        ) : null}

        {creating ? (
          <div className="stack-form tight" style={{ marginBottom: "1.25rem" }}>
            <label className="field">
              <span className="field-label">Name</span>
              <input
                className="input"
                value={name}
                placeholder="Washington CS, Grades 6-8"
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="field">
              <span className="field-label">Jurisdiction (optional)</span>
              <input
                className="input"
                value={jurisdiction}
                placeholder="Washington State"
                onChange={(e) => setJurisdiction(e.target.value)}
              />
            </label>
            <label className="field">
              <span className="field-label">Grade band (optional)</span>
              <input
                className="input"
                value={gradeBand}
                placeholder="6-8"
                onChange={(e) => setGradeBand(e.target.value)}
              />
            </label>
            <div className="teacher-modal-actions">
              <button type="button" className="btn primary" disabled={busy || !name.trim()} onClick={() => void handleCreate()}>
                {busy ? "Creating…" : "Create"}
              </button>
            </div>
          </div>
        ) : null}

        {loading ? (
          <p className="muted">Loading standard sets…</p>
        ) : sets.length === 0 ? (
          <p className="muted">No standard sets yet. Create one to get started.</p>
        ) : (
          <table className="teacher-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Standards</th>
                <th>Glossary</th>
                <th>Version</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sets.map((s) => (
                <tr key={s.id}>
                  <td>
                    <strong>{s.name}</strong>
                    {s.jurisdiction || s.gradeBand ? (
                      <p className="muted small">{[s.jurisdiction, s.gradeBand].filter(Boolean).join(" · ")}</p>
                    ) : null}
                  </td>
                  <td>
                    <span className="teacher-pill">{s.status}</span>
                  </td>
                  <td>{s.standardsCount}</td>
                  <td>{s.glossaryCount}</td>
                  <td>{s.currentVersion || "—"}</td>
                  <td className="teacher-table-actions">
                    <Link className="btn secondary tight" to={`/admin/standards/${s.id}`}>
                      Manage
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
    </div>
  );
}
